/**
 * Amazon Associates affiliate-link helpers.
 *
 * Scope (Phase 2.5 — scaffold only):
 *   - URL builder that takes an ISBN-10/13 + workspace Associates tag and
 *     returns an `amazon.{tld}/dp/{ASIN}?tag=...` link.
 *   - Disclosure resolver with a brand-safe default.
 *   - No PA-API calls. We do not look up live availability, prices, or covers.
 *     Workspaces wire up their own Associates account; Excerptus never owns
 *     the affiliate relationship.
 *
 * ISBN handling:
 *   - For physical books, Amazon's product page accepts ISBN-10 as the ASIN.
 *   - ISBN-13s starting with `978` can be converted back to ISBN-10 (other
 *     prefixes like `979` cannot — for those we skip linking rather than
 *     guess an invalid URL).
 */

export const DEFAULT_AMAZON_DISCLOSURE =
  "As an Amazon Associate I earn from qualifying purchases. " +
  "Some links in this post may be affiliate links — buying through them " +
  "supports this work at no extra cost to you.";

export const AMAZON_TAG_PATTERN = /^[A-Za-z0-9_-]{3,20}-(?:20|21|22)$/;

/** Strips spaces/dashes and uppercases. */
const cleanIsbn = (raw: string): string =>
  raw.replace(/[\s-]/g, "").toUpperCase();

/**
 * Convert a 13-digit ISBN starting with 978 to its ISBN-10 equivalent.
 * Returns null for any input we cannot safely convert.
 */
export const isbn13ToIsbn10 = (input: string): string | null => {
  const isbn = cleanIsbn(input);
  if (!/^\d{13}$/.test(isbn)) return null;
  if (!isbn.startsWith("978")) return null; // 979- has no ISBN-10 mapping
  const core = isbn.slice(3, 12); // 9 digits
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += Number(core[i]) * (10 - i);
  }
  const checkVal = (11 - (sum % 11)) % 11;
  const check = checkVal === 10 ? "X" : String(checkVal);
  return core + check;
};

/** Returns a 10-char ASIN-eligible ISBN, or null. */
export const isbnToAsin = (input: string | null | undefined): string | null => {
  if (!input) return null;
  const isbn = cleanIsbn(input);
  if (/^\d{9}[\dX]$/.test(isbn)) return isbn;
  if (/^\d{13}$/.test(isbn)) return isbn13ToIsbn10(isbn);
  return null;
};

export const isValidAssociatesTag = (tag: string | null | undefined): boolean =>
  !!tag && AMAZON_TAG_PATTERN.test(tag.trim());

export type AmazonMarketplace = "com" | "co.uk" | "ca" | "de" | "fr" | "com.au";

/**
 * Build an Amazon product URL with the workspace's Associates tag attached.
 * Returns null when the inputs cannot produce a safe affiliate link, so
 * callers can fall through to plain text.
 */
export const buildAmazonAffiliateUrl = (params: {
  isbn?: string | null;
  associatesTag?: string | null;
  marketplace?: AmazonMarketplace;
}): string | null => {
  const asin = isbnToAsin(params.isbn ?? null);
  if (!asin) return null;
  const tag = params.associatesTag?.trim();
  if (!isValidAssociatesTag(tag)) return null;
  const tld = params.marketplace ?? "com";
  return `https://www.amazon.${tld}/dp/${asin}?tag=${encodeURIComponent(tag!)}`;
};

export const resolveDisclosureText = (
  workspaceText: string | null | undefined,
): string => {
  const trimmed = workspaceText?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : DEFAULT_AMAZON_DISCLOSURE;
};

/**
 * Tooltip + aria-label copy used on every PA-API-confirmed affiliate title link.
 * Locked wording — do not edit without product approval.
 */
export const AFFILIATE_TOOLTIP =
  "Opens on Amazon — purchases support the author";

/**
 * Tailwind classes for the academic affiliate title link.
 *
 * Intentionally NO color class — the anchor inherits whatever the surrounding
 * `<a>` styling resolves to (the workspace/user's link color). The visual
 * signal is a subtle dotted underline that becomes solid on hover.
 */
export const AFFILIATE_LINK_CLASSNAME =
  "underline decoration-dotted underline-offset-2 hover:decoration-solid";

/**
 * Append (or replace) the `tag=` query param on a PA-API DetailPageURL while
 * preserving every other parameter. Returns the URL unchanged if the tag is
 * empty or invalid.
 */
export const appendAffiliateTag = (
  detailPageUrl: string,
  workspaceTag: string | null | undefined,
): string => {
  const tag = workspaceTag?.trim();
  if (!tag || !isValidAssociatesTag(tag)) return detailPageUrl;
  try {
    const u = new URL(detailPageUrl);
    u.searchParams.set("tag", tag);
    return u.toString();
  } catch {
    return detailPageUrl;
  }
};
